import type { PackedStick, PackMaterial } from './types';

/**
 * The pieces the selected materials cannot serve — the live-validation seam.
 *
 * A piece is unservable when it is longer than every stock length, which is
 * exactly the condition that makes `packSticks` return the all-NaN allocation,
 * so the recipe form can explain the empty allocation instead of showing
 * nothing.
 *
 * @param segmentLengths The snapped segment lengths, in real units.
 * @param stockLengths The selected materials' stock lengths, in real units.
 * @returns The unservable count, the longest such piece, and the largest stock.
 */
export const unservableSegments = (
	segmentLengths: number[],
	stockLengths: number[]
): { count: number; longest: number; maxStock: number } => {
	const maxStock = Math.max(0, ...stockLengths.filter((length) => Number.isFinite(length)));
	const unservable = segmentLengths.filter((length) => length > maxStock);

	return {
		count: unservable.length,
		longest: unservable.length ? Math.max(...unservable) : 0,
		maxStock,
	};
};

/**
 * Cross-bucket cutting: pieces are placed longest-first, each into the open
 * stick with the least sufficient offcut (best-fit); when none has room, the
 * piece opens the SMALLEST stock that holds it. Offcuts therefore serve later,
 * shorter pieces instead of being waste.
 *
 * ONE evaluation per ingredient: the return is the whole allocation, so there
 * is no per-material re-evaluation to keep consistent. The return SHAPE is
 * also the multi-material gate — a formula returning a list is asking for one
 * BOM line per material; scalar formulas keep the single-material path.
 *
 * Greedy, deterministic and pure, but not guaranteed optimal: a yield-aware
 * opening choice can beat smallest-fit when one bucket is deep and has no
 * partner pieces. That is the documented next refinement.
 *
 * ! VERSIONING: canvas recipes snapshot the FORMULA TEXT, so this name's
 * behaviour is frozen by the spec pins. Policy changes ship as a NEW name
 * (`packSticksV2`); genuine bugfixes update the pins deliberately in the same
 * PR. See PR #1859.
 *
 * @param maxCutsPerStick The shop-floor "max cuts per log": the most pieces
 *   one stick may be cut into. Omitted or ≤ 0 means the user declared nothing,
 *   so the pack takes the minimum-waste route — as many cuts as fit.
 * @returns Quantities INDEX-ALIGNED with `materials`. Two materials with the
 *   same length: the FIRST gets the allocation, the duplicate gets 0. Any
 *   piece that fits no stock makes EVERY element `NaN`: nothing is orderable
 *   until the catalogue is fixed, surfaced by live recipe validation.
 */
export const packSticks = (
	segments: number[],
	materials: PackMaterial[],
	maxCutsPerStick = 0
): number[] => packSticksDetailed(segments, materials, maxCutsPerStick).quantities;

/**
 * `packSticks` with its working shown: the same allocation, plus every stick
 * and the pieces cut from it — for the breakdown, never the formula. The
 * quantities ARE `packSticks`' (it delegates here), so the two cannot drift.
 *
 * @returns `quantities` as `packSticks` documents them; `sticks` empty when
 *   any piece is unservable.
 */
export const packSticksDetailed = (
	segments: number[],
	materials: PackMaterial[],
	maxCutsPerStick = 0
): { quantities: number[]; sticks: PackedStick[] } => {
	const limit = maxCutsPerStick > 0 ? maxCutsPerStick : Infinity;
	const pieces = [...segments].sort((a, b) => b - a);
	const sizes = materials.map((m) => m.length).sort((a, b) => a - b);
	const sticks: { capacity: number; used: number; cuts: number; pieces: number[] }[] = [];

	for (const piece of pieces) {
		const smallest = sizes.find((size) => size >= piece);
		if (smallest === undefined) return { quantities: materials.map(() => NaN), sticks: [] };

		let best: (typeof sticks)[number] | undefined;
		for (const candidate of sticks) {
			const room = candidate.capacity - candidate.used;
			if (
				room >= piece &&
				candidate.cuts < limit &&
				(!best || room < best.capacity - best.used)
			) {
				best = candidate;
			}
		}

		if (best) {
			best.used += piece;
			best.cuts += 1;
			best.pieces.push(piece);
		} else {
			sticks.push({ capacity: smallest, used: piece, cuts: 1, pieces: [piece] });
		}
	}

	const packed: PackedStick[] = [];
	const byLength = new Map<number, number>();
	for (const stick of sticks) {
		const final = sizes.find((size) => size >= stick.used);
		if (final === undefined) continue;
		byLength.set(final, (byLength.get(final) ?? 0) + 1);
		packed.push({ stock: final, pieces: stick.pieces });
	}

	// Duplicate lengths: the first material of a length takes the allocation.
	const seen = new Set<number>();
	const quantities = materials.map((material) => {
		if (seen.has(material.length)) return 0;
		seen.add(material.length);
		return byLength.get(material.length) ?? 0;
	});

	return { quantities, sticks: packed };
};
